import {
  createClient,
  isAuthRetryableFetchError,
  isAuthSessionMissingError,
  type AuthError,
  type Session,
} from '@supabase/supabase-js'

import { fetchWithTimeout } from '@/lib/fetch-with-timeout'
import {
  SyncRequestError,
  type SyncFailureReason,
} from '@/lib/sync-manager/sync-errors'
import type { SessionSource } from '@/clients/trpc-links.ts'

/**
 * How long a request to Supabase may take. A token refresh hung on a dead socket
 * holds the auth lock, and every session lookup waits behind it (K42); timed
 * out, it fails like a network error, which auth-js retries and which keeps the
 * session.
 */
export const SUPABASE_TIMEOUT_MS = 30_000

export const supabaseProjectId = import.meta.env.VITE_SUPABASE_URL?.match(
  /^https?:\/\/([^.]+)\.supabase\.co/i
)?.[1]

export const supabaseAuthStorageKey = `sb-${supabaseProjectId}-auth-token`

export const supabaseClient = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_KEY,
  {
    auth: {
      storageKey: supabaseAuthStorageKey,
    },
    global: {
      fetch: (input, init) =>
        fetchWithTimeout(input, init, SUPABASE_TIMEOUT_MS),
    },
  }
)

/** Why auth-js has no session: offline (`whenOffline`), none, or refused. */
const authFailure = (
  error: AuthError,
  whenOffline: SyncFailureReason
): SyncFailureReason =>
  isAuthSessionMissingError(error)
    ? 'no_session'
    : isAuthRetryableFetchError(error)
      ? whenOffline
      : 'rejected' // the auth server refused the refresh: auth-js signs out

/**
 * Runs `work` once for every caller that comes while it is in flight. Calls
 * refused together (a 401 each) need one refresh, not one each: auth-js takes
 * its lock per call and would refresh again for each of them.
 */
function shared<T>(work: () => Promise<T>): () => Promise<T> {
  let inFlight: Promise<T> | undefined
  return () =>
    (inFlight ??= work().finally(() => {
      inFlight = undefined
    }))
}

/** The session API calls are sent with (`sessionLink`, K44). */
export const supabaseSession: SessionSource = {
  token: shared(async () => {
    const { data, error } = await supabaseClient.auth.getSession()
    // An expired token whose refresh couldn't reach the auth server: offline,
    // not signed out
    if (!data.session && error) {
      throw new SyncRequestError(authFailure(error, 'network'), error.message)
    }
    return data.session?.access_token
  }),

  refresh: shared(async () => {
    const { data, error } = await supabaseClient.auth.refreshSession()
    if (data.session) return data.session.access_token
    throw new SyncRequestError(
      error ? authFailure(error, 'refresh_failed') : 'no_session',
      error?.message ?? 'no session'
    )
  }),
}

export function getSupabaseCachedUser() {
  try {
    const cachedSession = localStorage.getItem(supabaseAuthStorageKey)
    if (!cachedSession) return null

    const session = JSON.parse(cachedSession) as Session
    return session?.user
  } catch (error) {
    return null
  }
}
